import { HeaderBetweenGroups } from "@components/formGroup/FormGroup.styles";
import { getWorkDate } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, ifAny, IFieldDefFn, IGetValueArgs, not, TGetValueFn } from "@components/smart/FieldInfo";
import {
    AttachmentsAdditionalProperties,
    AttachmentsDef,
    BirthNumberFieldDef,
    getAddressFields,
    getCommonFilterDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { formAttachmentFieldDef } from "@components/smart/smartFileInput/SmartFileInput.utils";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { ICellValueObject, TCellValue } from "@components/table";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrAnnualTaxSettlementRequestEntity,
    IPrAtsrFormSectionDetailMortgageLocationEntity,
    PrAnnualTaxSettlementRequestEntity,
    PrAtsrFormSectionDetailChildDeductionEntity,
    PrAtsrFormSectionDetailMortgageLocationEntity,
    PrAtsrFormSectionDetailMortgageOtherPeopleEntity,
    PrAtsrFormSectionDetailPartnerTaxCreditEntity,
    PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity,
    PrAtsrFormSectionDetailZtpEntity,
    PrAtsrFormSectionDIPEntity,
    PrAtsrFormSectionEntity,
    PrAtsrFormSectionForeclosureEndBonusEntity,
    PrAtsrFormSectionGiftsEntity,
    PrAtsrFormSectionLifeInsuranceEntity,
    PrAtsrFormSectionLongTermCareInsuranceEntity,
    PrAtsrFormSectionMortgageInterestEntity,
    PrAtsrFormSectionOtherEmploymentRecordsEntity,
    PrAtsrFormSectionOtherEmploymentsEntity,
    PrAtsrFormSectionPartnerTaxCreditEntity,
    PrAtsrFormSectionPensionInsuranceEntity,
    PrAtsrFormSectionSupplementaryPensionSavingsNewEntity,
    PrAtsrFormSectionSupplementaryPensionSavingsOldEntity,
    PrEmployeeEntity,
    PrMortgageLocationAddressEntity,
    PrMortgageOtherPeopleAddressEntity,
    PrTaxpayerDeclarationDiscountEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAnnualTaxSettlementRequestResultTypeCode,
    PrAnnualTaxSettlementRequestStatusTypeCode,
    PrAnnualTaxSettlementStatusCode,
    PrIdentifierCode,
    PrPropertyAcquisitionCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { universalPersonNameFormatter, userNameWithAvatarFormatter } from "@pages/admin/users/Users.utils";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "@pages/numberRange/NumberRange.utils";
import {
    getAdditionalTaxDeclarationFieldsDef,
    getAdditionalTaxDeclarationGroups
} from "@pages/payroll/annualTaxSettlementRequest/AdditionalTaxDeclaration";
import { TaxpayerDeclarationOverviewTable } from "@pages/payroll/annualTaxSettlementRequest/TaxpayerDeclarationOverviewTable";
import { getEmployeeSelectDef } from "@pages/payroll/employee/Employee.utils";
import { getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import { getDefinitions as taxPayerDeclarationDef, } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isNewEntity } from "@utils/definition.utils";
import { logger } from "@utils/log";
import i18next from "i18next";
import React from "react";
import { Trans } from "react-i18next";
import { DefaultTheme } from "styled-components";

import { BasicInputSizes, FieldType, GroupedField, LabelStatus, Sort, TextAlign, ValidatorType } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BooleanType from "../../../types/Boolean";
import CurrencyType from "../../../types/Currency";
import { getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { FormDescriptionText } from "../../../views/formView/FormView.styles";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { IDefinition, IGetDefinition, TFieldDefinition, TFieldsDefinition } from "../../PageUtils";
import AnnualTaxSettlementRequestFormView, { IPrAnnualTaxSettlementRequestCustomData } from "./AnnualTaxSettlementRequestFormView";

function isMortgageInterestSectionActive({ storage }: IGetValueArgs): boolean {
    return storage.getValue(storage.data.bindingContext.navigate(PrAnnualTaxSettlementRequestEntity.MortgageInterest).navigate(PrAtsrFormSectionEntity.IsActive)) === true;
}

export function getThemeColorForResult(result: PrAnnualTaxSettlementRequestResultTypeCode): keyof DefaultTheme {
    switch (result) {
        case PrAnnualTaxSettlementRequestResultTypeCode.NotRequested:
            return "C_TEXT_primary";
        case PrAnnualTaxSettlementRequestResultTypeCode.Requested:
            return "C_SEM_text_good";
        case PrAnnualTaxSettlementRequestResultTypeCode.WaitingForDecision:
            return "C_SEM_text_warning";
    }
}

export function getThemeColorForStatus(status: PrAnnualTaxSettlementRequestStatusTypeCode): keyof DefaultTheme {
    switch (status) {
        case PrAnnualTaxSettlementRequestStatusTypeCode.IsVerified:
            return "C_SEM_text_good";
        case PrAnnualTaxSettlementRequestStatusTypeCode.ToBeVerified:
            return "C_SEM_text_warning";
        case PrAnnualTaxSettlementRequestStatusTypeCode.WaitingForDecision:
        case PrAnnualTaxSettlementRequestStatusTypeCode.NotRequested:
        case PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled:
            return "C_TEXT_primary";
    }
}

export function getThemeColorForSettlementStatus(status: PrAnnualTaxSettlementStatusCode): keyof DefaultTheme {
    switch (status) {
        case PrAnnualTaxSettlementStatusCode.Checked:
            return "C_SEM_text_good";
        case PrAnnualTaxSettlementStatusCode.ToCheck:
            return "C_SEM_text_warning";
        case PrAnnualTaxSettlementStatusCode.IncludedInPayslip:
        case PrAnnualTaxSettlementStatusCode.PaidOut:
            return "C_TEXT_primary";
    }
}

export function ATSRResultFormatter(_: TValue, args: IFormatOptions): TCellValue {
    const entity = args.entity as IPrAnnualTaxSettlementRequestEntity;
    return {
        value: <ColoredText
            color={getThemeColorForResult(entity.Result?.Code as PrAnnualTaxSettlementRequestResultTypeCode)}>
            {entity.Result?.Name}
        </ColoredText>,
        tooltip: entity.Result?.Name
    };
}

export function ATSRStatusFormatter(_: TValue, args: IFormatOptions): TCellValue {
    const entity = args.entity as IPrAnnualTaxSettlementRequestEntity;
    if (!entity.Status || [PrAnnualTaxSettlementRequestStatusTypeCode.WaitingForDecision, PrAnnualTaxSettlementRequestStatusTypeCode.NotRequested].includes(entity.Status.Code as PrAnnualTaxSettlementRequestStatusTypeCode)) {
        return null;
    }
    return {
        value: <ColoredText
            color={getThemeColorForStatus(entity.Status?.Code as PrAnnualTaxSettlementRequestStatusTypeCode)}>
            {entity.Status?.Name}
        </ColoredText>,
        tooltip: entity.Status?.Name
    };
}

const StatusInfoPath = BindingContext.localContext("PrAnnualTaxSettlementRequestStatusInfo");
export const mortgageInterestHasOtherPeoplePath = BindingContext.localContext("mortgageHasOtherPeople");
export const mortgageInterestLocationDescPath = BindingContext.localContext("mortgageInterestLocationDesc");
const taxpayerDeclarationOverviewTablePath = BindingContext.localContext("taxpayerDeclarationOverviewTable");
export const hasAdditionalTaxPayerDeclarationPath = BindingContext.localContext("hasAdditionalTaxPayerDeclaration");

export const otherEmploymentsGroupId = "OtherEmploymentsGroup";

const isRequested = ({ storage }: IGetValueArgs): boolean => {
    return storage.getValueByPath(PrAnnualTaxSettlementRequestEntity.ResultCode) === PrAnnualTaxSettlementRequestResultTypeCode.Requested;
};

const waitingForVerification = ({ storage }: IGetValueArgs): boolean => {
    return storage.getValueByPath(PrAnnualTaxSettlementRequestEntity.StatusCode) === PrAnnualTaxSettlementRequestStatusTypeCode.ToBeVerified;
};

const isVerified = ({ storage }: IGetValueArgs): boolean => {
    return storage.getValueByPath(PrAnnualTaxSettlementRequestEntity.StatusCode) === PrAnnualTaxSettlementRequestStatusTypeCode.IsVerified;
};

function otherEmployeePath(path: PrAtsrFormSectionOtherEmploymentRecordsEntity | string): string {
    return createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionOtherEmploymentsEntity.OtherEmployments, path);
}

function partnerTaxCreditPath(path: PrAtsrFormSectionDetailPartnerTaxCreditEntity): string {
    return createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.Detail, path);
}

function partnerTaxCreditZtpPath(path: PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity): string {
    return createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.DetailZtp, path);
}

function hasSection(section: string): (args: IGetValueArgs) => boolean {
    return (args: IGetValueArgs): boolean => {
        return true; // todo
        // return isNewEntity(args) || !isObjectEmpty(args.storage.getValueByPath(section));
    };
}

const togglePropDef: TFieldDefinition = {
    type: FieldType.Switch,
    labelStatus: LabelStatus.Removed,
    fieldSettings: {
        type: SwitchType.YesNo
    }
};

const partnerTaxCreditRangePath = BindingContext.localContext("partnerTaxCreditRange");
const partnerTaxCreditZtpRangePath = BindingContext.localContext("partnerTaxCreditZtpRange");
const partnerTaxCreditChildDescPath = BindingContext.localContext("partnerTaxCreditChildDesc");

const isSectionSwitched = (args: IGetValueArgs): boolean => {
    const { storage, bindingContext } = args;
    let sectionBc = bindingContext.getParent();
    while (!sectionBc.isValidNavigation("IsActive") || sectionBc.isRoot()) {
        sectionBc = sectionBc.getParent();
    }
    if (sectionBc.isRoot()) {
        // Should not happen
        logger.error("isSectionSwitched: Could not find section binding context");
        return false;
    }
    return storage.getValue(sectionBc.navigate(PrAtsrFormSectionEntity.IsActive)) === true;
};

const identifierCodeDef = (): TFieldDefinition => ({
    type: FieldType.ComboBox,
    label: i18next.t("AnnualTaxSettlementRequest:Form.IdentifierCode"),
    defaultValue: PrIdentifierCode.DateOfBirth,
    isRequired: true,
    groupedField: GroupedField.MultiStart,
    fieldSettings: {
        items: [{
            id: PrIdentifierCode.DateOfBirth,
            label: i18next.t("AnnualTaxSettlementRequest:Form.DateOfBirth")
        }, {
            id: PrIdentifierCode.PersonalIdentificationNumber,
            label: i18next.t("AnnualTaxSettlementRequest:Form.PersonalIdentificationNumber")
        }]
    },
    isVisible: isSectionSwitched
});

const identifierDateDef: TFieldDefinition = {
    labelStatus: LabelStatus.Removed,
    groupedField: GroupedField.MultiEnd,
    isVisible: (args) => {
        const codeBc = args.bindingContext.getParent().navigate("IdentifierCode");
        return args.storage.getValue(codeBc) === PrIdentifierCode.DateOfBirth && isSectionSwitched(args);
    },
    isRequired: true,
};

const identifierValueDef: TFieldDefinition = {
    ...BirthNumberFieldDef,
    labelStatus: LabelStatus.Removed,
    groupedField: GroupedField.MultiEnd,
    isVisible: (args) => {
        const codeBc = args.bindingContext.getParent().navigate("IdentifierCode");
        return args.storage.getValue(codeBc) === PrIdentifierCode.PersonalIdentificationNumber && isSectionSwitched(args);
    },
    isRequired: true,
};

export const sections = [
    PrAnnualTaxSettlementRequestEntity.OtherEmployments,
    PrAnnualTaxSettlementRequestEntity.Gifts,
    PrAnnualTaxSettlementRequestEntity.MortgageInterest,
    PrAnnualTaxSettlementRequestEntity.PensionInsurance,
    PrAnnualTaxSettlementRequestEntity.LifeInsurance,
    PrAnnualTaxSettlementRequestEntity.DIP,
    PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew,
    PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld,
    PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance,
    PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus,
    PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit
];

const togglePropDefinitions = sections.reduce((acc: TFieldsDefinition, section: string) => {
    acc[createPath(section, PrAtsrFormSectionEntity.IsActive)] = togglePropDef;
    return acc;
}, {});

const commonAttachmentsDef: TFieldDefinition = {
    ...formAttachmentFieldDef,
    isRequired: true,
    isVisible: isSectionSwitched,
};

function isSectionVisible(sectionPath: string): TGetValueFn<boolean> {
    return ({ storage }) => {
        return isSectionSwitched({
            storage,
            bindingContext: storage.data.bindingContext.navigate(sectionPath).navigate(PrAtsrFormSectionEntity.IsActive)
        });
    };
}

export enum AnnualTaxSettlementFormViewAction {
    CopyOtherEmployementData = "CopyOtherEmployementData"
}

export const getDefinitions: IGetDefinition = (context): IDefinition => {
    const amountAppliedDef: TFieldDefinition = {
        ...getPayrollCurrencyFieldDef(),
        label: i18next.t("AnnualTaxSettlementRequest:Form.AmountApplied"),
        isRequired: true,
        isVisible: isSectionSwitched,
        validator: {
            type: ValidatorType.Integer,
            settings: {
                min: 0
            }
        }
    };

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrAnnualTaxSettlementRequest}Table`,
        title: i18next.t("AnnualTaxSettlementRequest:Table.Title"),
        tabs: [],
        tabsSettings: {
            filterFieldName: PrAnnualTaxSettlementRequestEntity.Year
        },
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrAnnualTaxSettlementRequestEntity.NumberOurs,
                PrAnnualTaxSettlementRequestEntity.Employee,
                PrAnnualTaxSettlementRequestEntity.Result,
                PrAnnualTaxSettlementRequestEntity.Status,
                PrAnnualTaxSettlementRequestEntity.DateLastModified
            ],
            filterDefinition: {
                ...getCommonFilterDefs(),
                [PrAnnualTaxSettlementRequestEntity.NumberOurs]: {
                    label: i18next.t("AnnualTaxSettlementRequest:Table.NumberOurs"),
                },
                ...withDisplayName(PrAnnualTaxSettlementRequestEntity.Employee, PrEmployeeEntity.LastName, {
                    label: i18next.t("AnnualTaxSettlementRequest:Table.Employee"),
                    formatter: (_val: TValue, args: IFormatOptions) => {
                        return universalPersonNameFormatter((args.entity as IPrAnnualTaxSettlementRequestEntity).Employee);
                    },
                    additionalProperties: [
                        { id: PrEmployeeEntity.FirstName }
                    ]
                }),
                [PrAnnualTaxSettlementRequestEntity.DateLastModified]: {},
                ...withDisplayName(PrAnnualTaxSettlementRequestEntity.Result, "Name", {
                    label: i18next.t("AnnualTaxSettlementRequest:Table.Result"),
                }),
                ...withDisplayName(PrAnnualTaxSettlementRequestEntity.Status, "Name", {
                    label: i18next.t("AnnualTaxSettlementRequest:Table.Status"),
                }),
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrAnnualTaxSettlementRequestEntity.NumberOurs, sort: Sort.Desc }],
        columns: [
            PrAnnualTaxSettlementRequestEntity.NumberOurs,
            PrAnnualTaxSettlementRequestEntity.Employee,
            PrAnnualTaxSettlementRequestEntity.Result,
            PrAnnualTaxSettlementRequestEntity.Status,
            PrAnnualTaxSettlementRequestEntity.DateLastModified,
        ],
        columnDefinition: {
            [PrAnnualTaxSettlementRequestEntity.NumberOurs]: {
                label: i18next.t("AnnualTaxSettlementRequest:Table.NumberOurs"),
            },
            [PrAnnualTaxSettlementRequestEntity.Employee]: {
                label: i18next.t("AnnualTaxSettlementRequest:Table.Employee"),
                textAlign: TextAlign.Left,
                formatter: (val: TValue, args: IFormatOptions) => {
                    return userNameWithAvatarFormatter(val, {
                        ...args,
                        entity: (args.entity as IPrAnnualTaxSettlementRequestEntity).Employee
                    });
                },
                additionalProperties: [
                    { id: PrEmployeeEntity.FirstName },
                    { id: PrEmployeeEntity.LastName }
                ]
            },
            [PrAnnualTaxSettlementRequestEntity.Result]: {
                label: i18next.t("AnnualTaxSettlementRequest:Table.Result"),
                formatter: ATSRResultFormatter
            },
            [PrAnnualTaxSettlementRequestEntity.Status]: {
                label: i18next.t("AnnualTaxSettlementRequest:Table.Status"),
                formatter: ATSRStatusFormatter
            },
            [PrAnnualTaxSettlementRequestEntity.DateLastModified]: {
                label: i18next.t("AnnualTaxSettlementRequest:Table.DateLastModified"),
            }
        },
    };

    const summary: ISummaryItem[] = [{
        ...getNumberOursSummaryDef("AnnualTaxSettlementRequest"),
    }, {
        id: PrAnnualTaxSettlementRequestEntity.Employee,
        isVisible: not(isNewEntity),
        formatter: (val: TValue, args: IFormatOptions) => {
            return (userNameWithAvatarFormatter(val, {
                ...args,
                entity: (args.entity as IPrAnnualTaxSettlementRequestEntity).Employee
            }, { isBig: true }) as ICellValueObject)?.value;
        },
        additionalProperties: [
            { id: PrEmployeeEntity.FirstName },
            { id: PrEmployeeEntity.LastName }
        ]
    }, {
        id: PrAnnualTaxSettlementRequestEntity.Year,
        label: i18next.t("AnnualTaxSettlementRequest:Form.Year"),
        isVisible: not(isNewEntity)
    }, {
        id: PrAnnualTaxSettlementRequestEntity.Result,
        label: i18next.t("AnnualTaxSettlementRequest:Form.Result"),
        isVisible: not(isNewEntity),
        formatter: (_val: TValue, args: IFormatOptions) => {
            const entity = args.entity as IPrAnnualTaxSettlementRequestEntity;
            return <ColoredText
                color={getThemeColorForResult(entity.Result?.Code as PrAnnualTaxSettlementRequestResultTypeCode)}>
                {entity.Result?.Name}
            </ColoredText>;
        }
    }, {
        id: PrAnnualTaxSettlementRequestEntity.Status,
        label: i18next.t("AnnualTaxSettlementRequest:Form.Status"),
        isVisible: ifAll(isRequested, not(isNewEntity)),
        formatter: (_val: TValue, args: IFormatOptions<IPrAnnualTaxSettlementRequestEntity>) => {
            const entity = args.entity;
            return <ColoredText
                color={getThemeColorForStatus(entity.Status?.Code as PrAnnualTaxSettlementRequestStatusTypeCode)}>
                {entity.Status?.Name}
            </ColoredText>;
        }
    }];

    const form: IFormDef = {
        id: `${EntityTypeName.PrAnnualTaxSettlementRequest}Form`,
        title: i18next.t("AnnualTaxSettlementRequest:Form.Title"),
        translationFiles: getDefinitions.translationFiles,
        isReadOnly: isVerified,
        files: {
            ...AttachmentsDef,
            readOnlyAttachmentPaths: [
                createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionGiftsEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionLongTermCareInsuranceEntity.Attachments ),
                createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionSupplementaryPensionSavingsOldEntity.Attachments ),
                createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionSupplementaryPensionSavingsNewEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionLifeInsuranceEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionDIPEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionForeclosureEndBonusEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.Attachments),
                createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionOtherEmploymentsEntity.OtherEmployments, PrAtsrFormSectionOtherEmploymentRecordsEntity.Attachment),
            ]
        },
        isDeletable: true,
        formControl: AnnualTaxSettlementRequestFormView,
        getItemBreadCrumbText: (storage: Model) => {
            if (isNewEntity({ storage })) {
                return i18next.t("AnnualTaxSettlementRequest:Form.Title");
            }
            const employee = storage.getValueByPath(PrAnnualTaxSettlementRequestEntity.Employee);
            return `${storage.t("AnnualTaxSettlementRequest:Form.Title")} ${employee.FirstName} ${employee.LastName}`;
        },
        summary,
        additionalProperties: [
            ...AttachmentsAdditionalProperties,
            ...NumberRangeAdditionalProperties,
            { id: PrAnnualTaxSettlementRequestEntity.Year },
            {
                id: createPath(PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration, PrTaxpayerDeclarationEntity.Children),
            }, {
                id: createPath(PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration, PrTaxpayerDeclarationEntity.Discounts),
                additionalProperties: [{
                    id: PrTaxpayerDeclarationDiscountEntity.DiscountType
                }, {
                    id: PrTaxpayerDeclarationDiscountEntity.IncludedMonths
                }]
            },
            // data for other employment dialog
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodFrom) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodTo) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationSigned) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationMonths) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.IncomeAmount) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.PaidIncomeAmount) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.AdditionalIncomeFromFormerPeriodsAmount) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromPaidIncomeAmount) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromAdditionalIncomeAmount) },
            { id: otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.SumOfMonthlyBonusAmount) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.FirstName)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.LastName)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.ChildDeductionOrder)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateZtpFrom)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateZtpTo)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedFrom)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedTo)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.PersonalIdentificationNumber)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DateFrom)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DateTo)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.IsZtpP)) },
            { id: otherEmployeePath(createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DisabilityType)) },
        ],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("AnnualTaxSettlementRequest"),
            [StatusInfoPath]: {
                type: FieldType.Custom,
                isVisible: ifAll(not(isRequested), not(isNewEntity)),
                customizationData: {
                    useForCustomization: false
                },
                render: ({ storage }) => {
                    const resultCode = storage.getValueByPath(PrAnnualTaxSettlementRequestEntity.ResultCode);
                    return <div>{i18next.t(`AnnualTaxSettlementRequest:Form.${resultCode}Info`)}</div>;
                }
            },
            [PrAnnualTaxSettlementRequestEntity.Employee]: {
                ...getEmployeeSelectDef(),
                isVisible: isNewEntity,
                fieldSettings: {
                    ...getEmployeeSelectDef().fieldSettings,
                    itemsForRender: (origItems, args: IGetValueArgs) => {
                        const possibleEmployeesIds: Set<number> = (args.storage.getCustomData() as IPrAnnualTaxSettlementRequestCustomData).possibleEmployeesIds;

                        if (possibleEmployeesIds && possibleEmployeesIds.size > 0) {
                            return origItems?.filter(item => possibleEmployeesIds.has(Number(item.id)));
                        }

                        // If for some reason we don't have the possibleEmployeesIds, we return the original list to avoid
                        // blocking the user, even though it might contain employees that are not valid for selection
                        // hopefully BE validation will catch any invalid selections in this case
                        return origItems;
                    }
                }
            },
            [PrAnnualTaxSettlementRequestEntity.Year]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S,
                isVisible: isNewEntity,
                defaultValue: () => getUtcDayjs(getWorkDate()).get("year") - 1,
                fieldSettings: {
                    itemsFactory: async (): Promise<ISelectItem[]> => {
                        const currentYear = getUtcDayjs(getWorkDate()).year();
                        return [{
                            id: currentYear - 1,
                            label: (currentYear - 1).toString()
                        }, {
                            id: currentYear,
                            label: currentYear.toString()
                        }];
                    }
                }
            },
            // OtherEmployments
            [otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.EmployerName)]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.EmployerName"),
                groupedField: GroupedField.MultiStart,
                isReadOnly: true
            },
            [otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Attachment)]: {
                ...commonAttachmentsDef,
                groupedField: GroupedField.MultiEnd,
                textAlign: TextAlign.Left,
                isReadOnly: true,
                fieldSettings: {
                    multiple: false
                }
            },
            [otherEmployeePath(PrAtsrFormSectionOtherEmploymentRecordsEntity.IsFilled)]: {
                type: FieldType.Input,
                isReadOnly: true,
                isVisible: ifAny(isVerified, waitingForVerification),
                formatter: (val: TValue): TCellValue => {
                    const formattedValue = BooleanType.format(!!val);
                    return {
                        value: <ColoredText color={val ? "C_SEM_text_good" : "C_SEM_text_bad"}>
                            {formattedValue}
                        </ColoredText>,
                        tooltip: formattedValue
                    };
                }
            },
            // Gifts
            [createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionGiftsEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionGiftsEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.GiftReceipt")
            },

            // MortgageInterest
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.MortgageInterestAttachments")
            },
            [mortgageInterestHasOtherPeoplePath]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.MortgageInterestHasOtherPeople"),
                type: FieldType.Switch,
                isVisible: isSectionVisible(PrAnnualTaxSettlementRequestEntity.MortgageInterest),
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            },
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.FirstName)]: {},
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.LastName)]: {},
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.IdentifierCode)]: identifierCodeDef(),
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.DateIdentifier)]: identifierDateDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.IdentifierValue)]: identifierValueDef,
            ...getAddressFields(createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople, PrAtsrFormSectionDetailMortgageOtherPeopleEntity.Address)),
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations, PrAtsrFormSectionDetailMortgageLocationEntity.Amount)]: amountAppliedDef,
            ...getAddressFields(createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations, PrAtsrFormSectionDetailMortgageLocationEntity.Address)),
            [mortgageInterestLocationDescPath]: {
                type: FieldType.Custom,
                isVisible: isMortgageInterestSectionActive,
                render: (args: IFieldDefFn) => {
                    const locations = args.storage.getValueByPath(createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations));
                    const totalAmount = locations?.reduce((acc: number, loc: IPrAtsrFormSectionDetailMortgageLocationEntity) => acc + (loc.Amount || 0), 0) ?? 0;
                    const formattedAmount = CurrencyType.format(totalAmount, {
                        currency: getCompanyCurrency(args.storage.context),
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 0
                    });
                    return <FormDescriptionText>
                        <Trans i18nKey={"AnnualTaxSettlementRequest:Form.MortgageLocationDescription"}
                               values={{ amount: formattedAmount }}
                               components={{
                                   1: <b/>
                               }}/>
                    </FormDescriptionText>;
                }
            },
            [createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations, PrAtsrFormSectionDetailMortgageLocationEntity.PropertyAcquisition)]: {
                type: FieldType.ComboBox,
                isRequired: true,
                defaultValue: PrPropertyAcquisitionCode.Until20201231,
                fieldSettings: {
                    items: [{
                        id: PrPropertyAcquisitionCode.Until20201231,
                        label: i18next.t("AnnualTaxSettlementRequest:Form.Until20201231")
                    }, {
                        id: PrPropertyAcquisitionCode.From20210101,
                        label: i18next.t("AnnualTaxSettlementRequest:Form.From20210101")
                    }]
                }
            },


            [createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionSupplementaryPensionSavingsOldEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionSupplementaryPensionSavingsOldEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            [createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionSupplementaryPensionSavingsNewEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionSupplementaryPensionSavingsNewEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            // PensionInsurance
            [createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            // LifeInsurance
            [createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionLifeInsuranceEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionLifeInsuranceEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            // DIP
            [createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionDIPEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionDIPEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            [createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionLongTermCareInsuranceEntity.Amount)]: amountAppliedDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionLongTermCareInsuranceEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.InsuranceReceipt")
            },

            // ForeclosureEndBonus
            [createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionForeclosureEndBonusEntity.Amount)]: {
                ...amountAppliedDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.ForeclosureEndBonusAmountApplied"),
            },
            [createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionForeclosureEndBonusEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.ForeclosureEndBonusAttachment")
            },

            // PartnerTaxCredit
            [partnerTaxCreditRangePath]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.MonthRange"),
                type: FieldType.MonthRange,
                isRequired: true,
                useForValidation: true,
                fieldSettings: {
                    fromPath: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.FromMonth),
                    toPath: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ToMonth)
                },
                additionalProperties: [
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.FromMonth) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ToMonth) },
                ],
                isVisible: (args: IGetValueArgs): boolean => {
                    return !!args.storage.getValueByPath(createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionEntity.IsActive));
                }
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.FirstName)]: {
                isVisible: isSectionSwitched,
                isRequired: true,
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.LastName)]: {
                isVisible: isSectionSwitched,
                isRequired: true,
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.IdentifierCode)]: identifierCodeDef(),
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.DateIdentifier)]: identifierDateDef,
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.IdentifierValue)]: identifierValueDef,
            [createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.PartnerTaxCreditAttachment")
            },
            [partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.IsActive)]: {
                type: FieldType.Switch,
                isVisible: isSectionVisible(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit),
                label: i18next.t("AnnualTaxSettlementRequest:Form.ApplyZTP"),
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            },
            [partnerTaxCreditZtpRangePath]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.MonthRange"),
                type: FieldType.MonthRange,
                isRequired: true,
                fieldSettings: {
                    fromPath: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.FromMonth),
                    toPath: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.ToMonth)
                },
                additionalProperties: [
                    { id: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.FromMonth) },
                    { id: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.ToMonth) },
                ],
                isVisible: (args) => {
                    return isSectionVisible(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit)(args) &&
                        args.storage.getValueByPath(partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.IsActive)) === true;
                },
            },
            [partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.Attachments)]: {
                ...commonAttachmentsDef,
                label: i18next.t("AnnualTaxSettlementRequest:Form.PartnerTaxCreditZtpAttachment")
            },
            [partnerTaxCreditChildDescPath]: {
                type: FieldType.Custom,
                isVisible: isSectionVisible(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit),
                render: () => {
                    return <FormDescriptionText>{i18next.t("AnnualTaxSettlementRequest:Form.PartnerTaxCreditChildDescription")}</FormDescriptionText>;
                }
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildFirstName)]: {
                isVisible: isSectionSwitched,
                isRequired: true,
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildLastName)]: {
                isVisible: isSectionSwitched,
                isRequired: true,
            },
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildIdentifierCode)]: identifierCodeDef(),
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildDateIdentifier)]: identifierDateDef,
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildIdentifierValue)]: identifierValueDef,
            [partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.BirthCertificateAttachments)]: {
                ...commonAttachmentsDef
            },
            ...togglePropDefinitions,
            [taxpayerDeclarationOverviewTablePath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <TaxpayerDeclarationOverviewTable
                        storage={storage as FormStorage<IEntity, IPrAnnualTaxSettlementRequestCustomData>}/>;
                }
            },
            [hasAdditionalTaxPayerDeclarationPath]: {
                type: FieldType.Switch,
                label: i18next.t("AnnualTaxSettlementRequest:Form.AdditionalTaxPayerDeclaration"),
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            },
            ...getAdditionalTaxDeclarationFieldsDef(context),
            [BindingContext.localContext("TaxPayerDeclaration")]: {
                type: FieldType.Custom,
                isVisible: isRequested,
                render: ({ storage }) => {
                    // Header between sections
                    return (
                        <HeaderBetweenGroups>
                            {storage.t("AnnualTaxSettlementRequest:Headers.TaxPayerDeclaration")}
                        </HeaderBetweenGroups>
                    );
                }
            }
        },
        groups: [
            {
                id: "main",
                rows: [
                    [{ id: StatusInfoPath }]
                ]
            }, {
                id: "TaxPayer",
                rows: [[
                    { id: PrAnnualTaxSettlementRequestEntity.Employee },
                    { id: PrAnnualTaxSettlementRequestEntity.Year },
                ]]
            },
            {
                id: otherEmploymentsGroupId,
                title: i18next.t("AnnualTaxSettlementRequest:Sections.OtherEmployments"),
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.OtherEmployments)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionEntity.IsActive),
                isSmallBottomMargin: true,
                lineItems: {
                    additionalFields: [],
                    collection: createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionOtherEmploymentsEntity.OtherEmployments),
                    order: "Id",
                    canAdd: args => args.storage.getValueByPath(createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionEntity.IsActive)),
                    isItemVisible: args => args.storage.getValueByPath(createPath(PrAnnualTaxSettlementRequestEntity.OtherEmployments, PrAtsrFormSectionEntity.IsActive)),
                    lineItemActions: [{
                        id: AnnualTaxSettlementFormViewAction.CopyOtherEmployementData,
                        label: i18next.t("AnnualTaxSettlementRequest:Form.CopySettlementData"),
                        iconName: "Update",
                    }],
                    isTheOnlyItemRemovable: true,
                    canReorder: false,
                    isItemCloneable: false,
                    columns: [
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.EmployerName },
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.Attachment },
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.IsFilled },
                    ]
                },
                rows: []
            },
            {
                id: "Gifts",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.Gifts"),
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.Gifts)),
                isSmallBottomMargin: true,
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionGiftsEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.Gifts, PrAtsrFormSectionGiftsEntity.Attachments) },
                ]]
            },
            {
                id: "MortgageInterest",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.MortgageInterest"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.MortgageInterest)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionEntity.IsActive),
                rows: [[{ id: mortgageInterestLocationDescPath }]],
                lineItems: {
                    additionalFields: [],
                    collection: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations),
                    order: "Id",
                    canReorder: false,
                    canAdd: isMortgageInterestSectionActive,
                    isItemVisible: isMortgageInterestSectionActive,
                    isItemCloneable: false,
                    columns: [
                        { id: createPath(PrAtsrFormSectionDetailMortgageLocationEntity.Address, PrMortgageLocationAddressEntity.Street) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageLocationEntity.Address, PrMortgageLocationAddressEntity.City) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageLocationEntity.Address, PrMortgageLocationAddressEntity.PostalCode) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageLocationEntity.Address, PrMortgageLocationAddressEntity.Country) },
                        { id: PrAtsrFormSectionDetailMortgageLocationEntity.PropertyAcquisition },
                        { id: PrAtsrFormSectionDetailMortgageLocationEntity.Amount },
                    ]
                }
            },
            {
                id: "MortgageInterest2",
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, isMortgageInterestSectionActive),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionEntity.IsActive),
                rows: [[{ id: mortgageInterestHasOtherPeoplePath }]],
                lineItems: {
                    additionalFields: [],
                    collection: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.OtherPeople),
                    order: "Id",
                    useLabelWrapping: false,
                    canAdd: args => args.storage.getValueByPath(mortgageInterestHasOtherPeoplePath) && isMortgageInterestSectionActive(args),
                    isItemVisible: args => args.storage.getValueByPath(mortgageInterestHasOtherPeoplePath) && isMortgageInterestSectionActive(args),
                    canReorder: false,
                    isItemCloneable: false,
                    columns: [
                        { id: PrAtsrFormSectionDetailMortgageOtherPeopleEntity.FirstName },
                        { id: PrAtsrFormSectionDetailMortgageOtherPeopleEntity.LastName },
                        { id: PrAtsrFormSectionDetailMortgageOtherPeopleEntity.IdentifierCode },
                        { id: PrAtsrFormSectionDetailMortgageOtherPeopleEntity.DateIdentifier },
                        { id: PrAtsrFormSectionDetailMortgageOtherPeopleEntity.IdentifierValue },
                        { id: createPath(PrAtsrFormSectionDetailMortgageOtherPeopleEntity.Address, PrMortgageOtherPeopleAddressEntity.Street) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageOtherPeopleEntity.Address, PrMortgageOtherPeopleAddressEntity.City) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageOtherPeopleEntity.Address, PrMortgageOtherPeopleAddressEntity.PostalCode) },
                        { id: createPath(PrAtsrFormSectionDetailMortgageOtherPeopleEntity.Address, PrMortgageOtherPeopleAddressEntity.Country) },
                    ]
                }
            },
            {
                id: "MortgageInterest3",
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.MortgageInterest), isMortgageInterestSectionActive),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Attachments) }
                ]]
            },
            {
                id: "LongTermInsurance",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.LongTermInsurance"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionLongTermCareInsuranceEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.LongTermCareInsurance, PrAtsrFormSectionLongTermCareInsuranceEntity.Attachments) }
                ]]
            },
            {
                id: "SupplementaryPensionSavingsNew",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.SupplementaryPensionSavingsNew"),
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionSupplementaryPensionSavingsNewEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsNew, PrAtsrFormSectionSupplementaryPensionSavingsNewEntity.Attachments) },
                ]]
            },
            {
                id: "PensionPlan",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.PensionPlan"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.PensionInsurance)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.PensionInsurance, PrAtsrFormSectionPensionInsuranceEntity.Attachments) }
                ]]
            },
            {
                id: "LifeInsurance",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.LifeInsurance"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.LifeInsurance)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionLifeInsuranceEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.LifeInsurance, PrAtsrFormSectionLifeInsuranceEntity.Attachments) }
                ]]
            },
            {
                id: "Dip",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.Dip"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.DIP)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionDIPEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.DIP, PrAtsrFormSectionDIPEntity.Attachments) }
                ]]
            },
            {
                id: "SupplementaryPensionSavingsOld",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.SupplementaryPensionSavingsOld"),
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionSupplementaryPensionSavingsOldEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.SupplementaryPensionSavingsOld, PrAtsrFormSectionSupplementaryPensionSavingsOldEntity.Attachments) },
                ]]
            },
            {
                id: "ForeclosureEnd",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.ForeclosureEnd"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionForeclosureEndBonusEntity.Amount) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.ForeclosureEndBonus, PrAtsrFormSectionForeclosureEndBonusEntity.Attachments) }
                ]]
            },
            {
                id: "PartnerTaxCredit",
                title: i18next.t("AnnualTaxSettlementRequest:Sections.PartnerTaxCredit"),
                isSmallBottomMargin: true,
                isVisible: ifAll(isRequested, hasSection(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit)),
                togglePropPath: createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionEntity.IsActive),
                rows: [[
                    { id: partnerTaxCreditRangePath },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.FirstName) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.LastName) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.IdentifierCode) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.IdentifierValue) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.DateIdentifier) },
                    { id: createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.Attachments) }
                ], [
                    { id: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.IsActive) },
                ], [
                    { id: partnerTaxCreditZtpRangePath }, { id: partnerTaxCreditZtpPath(PrAtsrFormSectionDetailPartnerTaxCreditZtpEntity.Attachments) }
                ], [
                    { id: partnerTaxCreditChildDescPath }
                ], [
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildFirstName) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildLastName) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildIdentifierCode) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildDateIdentifier) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.ChildIdentifierValue) },
                    { id: partnerTaxCreditPath(PrAtsrFormSectionDetailPartnerTaxCreditEntity.BirthCertificateAttachments) },
                ]]
            },
            {
                id: "TaxPayerDeclaration",
                isSmallBottomMargin: true,
                isVisible: isRequested,
                rows: [[
                    { id: BindingContext.localContext("TaxPayerDeclaration") }
                ], [
                    { id: taxpayerDeclarationOverviewTablePath }
                ], [
                    { id: hasAdditionalTaxPayerDeclarationPath }
                ]]
            },
            ...getAdditionalTaxDeclarationGroups(context)
        ]
    };

    return {
        entitySet: EntitySetName.PrAnnualTaxSettlementRequests,
        table,
        form
    };
};

getDefinitions.translationFiles = [
    "AnnualTaxSettlementRequest",
    ...taxPayerDeclarationDef.translationFiles,
    "Common",
    getEnumNameSpaceName(EntityTypeName.PrAnnualTaxSettlementRequestStatusType),
    getEnumNameSpaceName(EntityTypeName.PrAnnualTaxSettlementRequestResultType),
];
setDefByEntityType(EntityTypeName.PrAnnualTaxSettlementRequest, getDefinitions);